import { GridRowParams } from "@mui/x-data-grid";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { Table, DYNAMIC_GRID_TABLE_HEIGHT } from "components/Table";
import { useBrowserStorage } from "hooks";
import { downloadCSV } from "utils/file.utils";

import { useGetAssetsQuery } from "./asset-list.hooks";
import { Filters, Header, Toolbar } from "./components";
import { formatAsset, getColumns } from "./helpers";

export const AssetList = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [hiddenColumns, setHiddenColumns] = useBrowserStorage<string[]>(
        "assetsListHiddenColumns",
        ["performance", "classification"]
    );

    const { assets, loading, filterProps, tablePaginationProps, handleExport } =
        useGetAssetsQuery(hiddenColumns as any);

    const handleRowClick = useCallback(
        (props: GridRowParams) =>
            navigate(`/location-overview/${props.row.id}`),
        [navigate]
    );
    const columns = useMemo(() => getColumns(t), [t]);

    const onDownload = useCallback(() => {
        toast.info(`${t("common.download", "Download")}...`);

        handleExport()
            .then((res) => {
                const data = res.data?.getAssets ?? [];
                const rows = formatAsset(data);
                const filteredColumns = columns.filter(
                    (column) => !hiddenColumns.includes(column.field)
                );
                const header = filteredColumns.map(
                    (column) => column.headerName
                );

                const csvRows = rows.map((row) =>
                    filteredColumns.map((column) => {
                        const value = row[column.field as keyof typeof row];
                        const formatter = column.valueFormatter as any;

                        return formatter ? formatter(value) : value ?? "-";
                    })
                );

                const csv = [header, ...csvRows];
                downloadCSV(csv, `asset-list.csv`);
            })
            .catch((err) => {
                console.log(err);
                toast.error(
                    t(
                        "common.toast.error",
                        "Something went wrong, please try again"
                    )
                );
            });
    }, [columns, hiddenColumns, handleExport, t]);

    const slots = useMemo(
        () => ({
            toolbar: () => <Toolbar handleExport={onDownload} />,
        }),
        [onDownload]
    );
    return (
        <>
            <Header />

            <Filters {...filterProps} />

            <Table
                isLoading={loading}
                columns={columns}
                rows={formatAsset(assets)}
                onRowClick={handleRowClick}
                setCustomHeight={DYNAMIC_GRID_TABLE_HEIGHT}
                /* Pagination fields */
                paginationMode="server"
                sortingMode="server"
                {...tablePaginationProps}
                /** Toolbar **/
                slots={slots}
                slotProps={{
                    loadingOverlay: { variant: "linear-progress" },
                }}
                initialHiddenColumns={hiddenColumns}
                updateHiddenColumns={setHiddenColumns}
            />
        </>
    );
};
